/**
 * Order Information enhancements for the 17TRACK order tracking page
 * (/apps/17TRACK). Purely-additive behaviors layered on top of 17TRACK's own
 * rendering — none of them ever touch/remove anything else 17TRACK renders,
 * and all fail silently on any mismatch so the tracking page never breaks:
 *
 * 1. Destination city/state — 17TRACK's carrier feed only reports destination
 *    at country level. Looks up the real ship-to city/state via a backend
 *    endpoint (order + tracking number required, matched against Shopify
 *    before returning anything) and appends it next to 17TRACK's own
 *    "Destination: United States" text. Rebuilt from the value element's
 *    true original text every time rather than appended onto whatever's
 *    currently there — a customer looking up a second, different order on
 *    the same page load (no full refresh) changes the cached destination
 *    mid-session, and appending blindly stacked the new city/state onto
 *    the previous one still sitting in the text. Starting a new lookup
 *    also resets ebookDetected/suppressedTabTexts (#7/#9) for the same
 *    reason — otherwise a non-eBook order looked up right after an eBook
 *    order would keep showing the eBook banner. Also forced wrap-safe
 *    (white-space/word-break/min-width:0) since the appended text can run
 *    much longer than 17TRACK's original short value, which was
 *    overflowing past the card's edge on mobile instead of wrapping.
 *
 * 2. Free eBook download link — the free "22 Pound Margin" bundle bonus
 *    (3 quantity-tier products, all $0) renders as a line item whose title
 *    links to a bare Shopify product page that isn't a real download
 *    experience. That link is removed entirely and replaced with an obvious
 *    "Download the eBook here" button pointing straight at the PDF (the same
 *    CDN file already used in the Order confirmation email's eBook button).
 *
 * 3. Order Information panel — force-expanded and the "Show More/Show Less"
 *    toggle hidden entirely, so customers always see everything with no
 *    control to (accidentally or otherwise) collapse it.
 *
 * 4. Digital-only tracking message — when the current package is the eBook
 *    (detected by the presence of the download button from #2), 17TRACK's
 *    generic "Tracking information isn't available yet. Please wait." is
 *    replaced with a message that reflects reality: nothing ships, delivery
 *    is automatic. Never touches this message for the physical AFM Disabler
 *    package, which legitimately has real carrier tracking.
 *
 * 5. Package #1/#2 tab styling — solid volt-blue pills instead of 17TRACK's
 *    barely-visible outline. Styled directly via inline styles on whatever
 *    element's exact text is "Package #N" (found by walking the DOM, same as
 *    the Destination row above) rather than a guessed CSS class name — an
 *    earlier stylesheet-based attempt targeting `.ant-tabs-tab` had no
 *    effect, meaning this page doesn't use standard Ant Design tab classes.
 *    Which tab is "active" is tracked via our own click listener rather than
 *    read from 17TRACK's state, for the same reason. The WHOLE switcher is
 *    hidden (not just individually suppressed tabs) when only one real
 *    package remains — a single "Package #1" pill with nothing to switch
 *    to serves no purpose. Multiple tabs only ever show when the order
 *    genuinely has multiple physical tracking numbers.
 *
 * 6. Hide Destination on the eBook package — a ship-to city/state doesn't
 *    mean anything for a digital download, so the whole row is hidden
 *    (same isDigitalPackage signal as #4) rather than shown with an address
 *    that has nothing to do with the eBook.
 *
 * 7. Suppress digital-only packages as their own tabs — when the eBook or
 *    Shipping Protection lands ALONE in its own package/tab (separate from
 *    the AFM Disabler), that tab has nothing worth a dedicated view. Two
 *    detection paths:
 *      a) Backend-driven (primary) — the destination lookup (#1) also
 *         returns hasEbook and physicalPackageCount, computed server-side
 *         from Shopify's fulfillment data. Since that lookup only ever runs
 *         once we're already viewing the physical package, a
 *         physicalPackageCount of 1 means every OTHER tab must be
 *         digital-only, so they're hidden immediately — no need to visit
 *         them, and no flicker. If physicalPackageCount is more than 1
 *         (the order genuinely ships in multiple physical packages), this
 *         deliberately does nothing, since we can't safely tell which
 *         extra tab is the one to hide.
 *      b) Reactive fallback — if a digital-only tab (detected generically:
 *         eBook download button, or "Shipping Protection" text, AND
 *         exactly one line item in that package) is ever actually visited
 *         before (a) has run, it's suppressed and the customer is
 *         redirected to another tab on the spot.
 *    Either way, if the eBook was the one suppressed, its download link
 *    moves to the persistent banner (#9) instead of disappearing with the
 *    tab. The whole tab switcher stays hidden while (a)'s lookup is still
 *    in flight (tracked by tabsSettled) — otherwise every tab would flash
 *    visible for the round-trip's duration before the suppressed one
 *    disappeared.
 *
 * 8. Un-link "Powered by 17TRACK" — same treatment as the eBook link (#2):
 *    removed entirely rather than just restyled, text left in place.
 *
 * 9. Persistent eBook download banner — once a digital-only eBook package
 *    has been suppressed (#7), its download button reappears as a section
 *    INSIDE the Order Information card (first child of
 *    .yq-track-order-info-wrap, divider below it) rather than a separate
 *    box, visible no matter which package tab is currently active, so the
 *    download stays reachable without needing its own tab. Two earlier
 *    placements were tried and broke: anchoring near the tab switcher
 *    landed on the wrong element once only one tab remained visible, and
 *    inserting as a sibling BEFORE the card got caught by that card's
 *    parent grid/flex layout and rendered as a second box side-by-side
 *    with Order Information instead of stacking above it. Uses a
 *    DIFFERENT class name than the per-tab download button (#2)
 *    deliberately — several checks above use that class to mean "this
 *    specific tab's line item is the eBook", and this banner is
 *    sitewide, not tab-specific.
 *
 * 10. Center the "Tracking..." loading state — the text + package icon
 *     17TRACK shows while a lookup is running weren't aligned as a unit.
 *     Matched by exact text, centered via text-align + auto-margining
 *     EVERY sibling of the text (not just <img>/<svg> — an earlier version
 *     only matched those two tags, missing the icon whenever 17TRACK
 *     rendered it as some other element). Deliberately never touches
 *     `display` on the container — an earlier version forced
 *     display:flex !important on it, which made the indicator appear
 *     permanently (even on the bare form page, no lookup running) because
 *     17TRACK shows/hides that same container by toggling its own display
 *     property, and the override defeated that toggle.
 *
 * All content searches (#1, #4, #2's link matching, #8) are scoped to
 * VISIBLE elements only (isVisible: offsetWidth/offsetHeight/
 * getClientRects), not just textContent matches — if 17TRACK keeps an
 * inactive package tab's content sitting in the DOM rather than removing
 * it (just hidden), an unscoped search could match/edit that hidden
 * content instead of whatever's actually on screen. #5's tab-switcher
 * hiding also checks the visible "Tracking..." indicator (#10) directly,
 * not just the lookedUpFor/tabsSettled gate — 17TRACK renders the
 * Package #N tabs before the "Tracking Number:" text that gate depends
 * on, so the gate alone left a brief window where both tabs would flash
 * visible before a lookup had even started.
 *
 * All of these have to survive 17TRACK re-rendering the Order Information /
 * Tracking Information blocks on every package-tab switch, so all are
 * re-applied on every DOM mutation rather than injected once — via a single
 * MutationObserver on document.body, throttled to at most one scan per
 * animation frame (scheduleScan/scanScheduled) so a burst of mutations
 * can't run every check many times per second and saturate the main
 * thread. The two places that call .click() programmatically (#3's toggle,
 * #7's tab redirect) additionally guard against re-clicking the SAME
 * element more than once (clickedToggles WeakSet / text-based
 * suppressedTabTexts check) — repeat-clicking one element across
 * rapid-fire scans, if 17TRACK's own re-render doesn't update
 * synchronously, was visibly freezing other page animations.
 */
(function () {
  "use strict";

  if (!/\/apps\/17/i.test(window.location.pathname)) return;

  var API_BASE = "https://elycera-chat-agent.onrender.com";
  var EBOOK_PDF_URL =
    "https://cdn.shopify.com/s/files/1/0788/2050/7886/files/The_22_Pound_Margin.pdf?v=1783799097";
  // Product handles behind the free eBook's 3 quantity tiers (all share SKU "eBook").
  var EBOOK_PRODUCT_HANDLES = [
    "digital-product",
    "the-22-pound-margin-x-2",
    "the-22-pound-margin-x-3",
  ];

  var lookedUpFor = null; // "order|tracking" already sent to the API
  var cachedDestination = null; // {city, province} from the last successful lookup
  var DIGITAL_TRACKING_MESSAGE =
    "This is a digital product — delivered automatically. See your download below.";

  // Toggle/tab DOM elements we've already called .click() on — prevents
  // re-clicking the SAME element repeatedly across rapid-fire scans if
  // 17TRACK's own re-render doesn't update synchronously. A fresh element
  // instance (genuine later re-render) is unaffected, since WeakSet
  // membership is per object reference.
  var clickedToggles = new WeakSet();

  // False while a destination lookup is in flight for the current order —
  // that lookup is also what tells us which tabs (if any) to suppress, so
  // the whole tab switcher stays hidden until it resolves rather than
  // showing every tab and then having one disappear a moment later.
  var tabsSettled = false;

  function normalizeWhitespace(str) {
    return (str || "").replace(/\s+/g, " ").trim();
  }

  // Unlike textContent, offsetWidth/offsetHeight/getClientRects are all
  // zero for anything hidden via display:none (on itself or an ancestor).
  // If 17TRACK keeps an inactive tab's content sitting in the DOM (just
  // hidden) rather than removing it, textContent-based searches would
  // match/edit THAT hidden content instead of whatever's actually on
  // screen — this scopes content searches to only what's really visible.
  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  // ---------- Destination city/state ----------

  function findDestinationLabel() {
    var scope =
      document.querySelector(".yq-track-order-info-wrap") || document.body;
    var walker = document.createTreeWalker(scope, NodeFilter.SHOW_ELEMENT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (node.children.length) continue; // leaf elements only
      if (!isVisible(node)) continue;
      if (normalizeWhitespace(node.textContent) === "Destination") {
        return node;
      }
    }
    return null;
  }

  function findValueElement(labelEl) {
    var row =
      labelEl.closest(".yq-track-order-info-item") || labelEl.parentElement;
    if (!row) return null;
    var leaves = row.querySelectorAll("*");
    var passedLabel = false;
    for (var i = 0; i < leaves.length; i++) {
      var el = leaves[i];
      if (el === labelEl) {
        passedLabel = true;
        continue;
      }
      if (passedLabel && !el.children.length && normalizeWhitespace(el.textContent)) {
        return el;
      }
    }
    return null;
  }

  // Re-applies the cached destination text whenever it's missing — covers
  // the initial render AND every later re-render (Show More/Less toggle,
  // switching package tabs) that replaces the row with fresh 17TRACK markup.
  //
  // Always rebuilds from the value element's TRUE original text (captured
  // once into a data attribute, since a freshly re-rendered element has
  // none yet) rather than appending onto whatever's currently there. A
  // customer looking up a second tracking number on the same page load
  // (no full refresh) makes cachedDestination change mid-session — appending
  // blindly stacked the new city/state onto the previous one still sitting
  // in the text ("Owens Cross Roads, Alabama — Fuquay Varina, North
  // Carolina") instead of replacing it.
  function applyCachedDestination() {
    if (!cachedDestination) return;
    var parts = [cachedDestination.city, cachedDestination.province].filter(Boolean);
    if (!parts.length) return;

    var labelEl = findDestinationLabel();
    var valueEl = labelEl && findValueElement(labelEl);
    if (!valueEl) return;

    if (!valueEl.dataset.elyOriginalText) {
      var current = normalizeWhitespace(valueEl.textContent);
      if (!current) return;
      valueEl.dataset.elyOriginalText = current;
    }

    var desired = valueEl.dataset.elyOriginalText + " — " + parts.join(", ");
    if (normalizeWhitespace(valueEl.textContent) !== desired) {
      valueEl.textContent = desired;
    }

    // The appended city/state can make this value much longer than
    // whatever short "United States"-length text 17TRACK's own layout was
    // designed to fit — on narrow (mobile) widths that was overflowing
    // past the card's right edge instead of wrapping. min-width:0 matters
    // here specifically because this is a flex item; flex items default
    // to min-width:auto, which can block wrapping and force overflow
    // instead, even with white-space/word-break set correctly.
    valueEl.style.setProperty("white-space", "normal", "important");
    valueEl.style.setProperty("word-break", "break-word", "important");
    valueEl.style.setProperty("overflow-wrap", "break-word", "important");
    valueEl.style.setProperty("max-width", "100%", "important");
    valueEl.style.setProperty("min-width", "0", "important");
    valueEl.style.setProperty("text-align", "right", "important");

    var row = valueEl.closest(".yq-track-order-info-item");
    if (row) {
      row.style.setProperty("flex-wrap", "wrap", "important");
    }
  }

  // Real computed-style data (not guessed) confirmed 17TRACK's own layout
  // has near-zero side padding on EVERY row in this card (row spans 368px
  // of a 370px-wide wrap), not just Destination — it's just the only one
  // long enough to visibly reach the edge normally. Applied to every row
  // (Destination, Transport Time, Order Number, ...) AND the product line
  // item uniformly, rather than fixing them one at a time as each gets
  // reported, and only on narrow (mobile) widths where this actually
  // matters.
  function fixOrderInfoMobileSpacing() {
    if (window.innerWidth > 480) return;
    var wrap = document.querySelector(".yq-track-order-info-wrap");
    if (!wrap) return;

    var rows = wrap.querySelectorAll(".yq-track-order-info-item, .yq-track-product-item");
    rows.forEach(function (row) {
      row.style.setProperty("box-sizing", "border-box", "important");
      row.style.setProperty("padding-left", "10px", "important");
      row.style.setProperty("padding-right", "10px", "important");
      row.style.setProperty("font-size", "13px", "important");
    });

    // "Order Information" heading — same zero-side-padding layout issue,
    // matched by exact text like everything else in this file since it
    // doesn't have a distinct class of its own to target reliably.
    var walker = document.createTreeWalker(wrap, NodeFilter.SHOW_ELEMENT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (node.children.length) continue;
      if (!isVisible(node)) continue;
      if (normalizeWhitespace(node.textContent) === "Order Information") {
        node.style.setProperty("box-sizing", "border-box", "important");
        node.style.setProperty("padding-left", "10px", "important");
        node.style.setProperty("padding-right", "10px", "important");
        // Vertical gap before the product thumbnail/row below it, which
        // was rendering flush against the heading with no breathing room.
        node.style.setProperty("margin-bottom", "12px", "important");
        break;
      }
    }
  }

  function lookupDestination(orderNumber, trackingNumber) {
    var key = orderNumber + "|" + trackingNumber;
    if (lookedUpFor === key) return;
    lookedUpFor = key;
    tabsSettled = false;

    // Looking up a second, different order on the same page load (no full
    // refresh) must not carry over state from the previous one — same bug
    // class as the destination stacking fix. Without this, a non-eBook
    // order looked up right after an eBook order would keep showing the
    // eBook banner (ebookDetected stuck true) and could keep a stale
    // suppressed-tab entry from the previous order's "Package #N" text.
    ebookDetected = false;
    suppressedTabTexts = {};
    var staleBanner = document.querySelector(".ely-ebook-persistent-banner");
    if (staleBanner) staleBanner.parentNode.removeChild(staleBanner);

    var url =
      API_BASE +
      "/api/order-destination?order=" +
      encodeURIComponent(orderNumber) +
      "&tracking=" +
      encodeURIComponent(trackingNumber);

    fetch(url)
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (!data || data.error) return;

        if (data.city || data.province) {
          cachedDestination = { city: data.city, province: data.province };
          applyCachedDestination();
        }

        if (data.hasEbook) ebookDetected = true;

        // We're necessarily on the physical package right now (this lookup
        // only ever runs once a real tracking number is on screen) — if
        // Shopify confirms that's the ONLY physical fulfillment on the
        // order, every other tab must be digital-only, so hide them
        // immediately without needing to visit each one to find out. If the
        // order genuinely ships in multiple physical packages, this is
        // deliberately left alone — the reactive per-tab check below still
        // catches a genuinely digital-only tab safely if it's ever visited.
        if (data.physicalPackageCount === 1 && activeTabText) {
          findPackageTabLabels().forEach(function (l) {
            var text = normalizeWhitespace(l.textContent);
            if (text !== activeTabText) suppressedTabTexts[text] = true;
          });
        }
      })
      .catch(function () {
        // Silent: the tracking page must never look broken because of this.
      })
      .then(function () {
        // Runs after either branch above (success or caught error) — tabs
        // were hidden the moment this lookup started specifically so they
        // wouldn't flash both-visible before this resolved; now that it
        // has, reveal them (correctly suppressed or not) either way.
        tabsSettled = true;
      });
  }

  // Forces the order-info panel open and hides the Show More/Show Less
  // toggle entirely — there's nothing left to toggle, so the control itself
  // goes away rather than just defaulting to expanded. Re-applied on every
  // scan since 17TRACK re-renders a fresh, visible toggle on every mutation
  // (tab switch, etc.). Handles ALL matching toggles, not just the first —
  // looking up a second tracking number while the previous result is still
  // on screen can leave more than one in the DOM briefly, and querying only
  // the first risked missing the one actually still visible.
  function forceExpandOrderInfo() {
    var toggles = document.querySelectorAll(".yq-track-order-show-toggle");
    toggles.forEach(function (toggle) {
      // Click each distinct DOM element AT MOST ONCE, ever — if 17TRACK's
      // click handler doesn't update the label synchronously, re-checking
      // "does it still say SHOW MORE" on the very next scan (which can
      // fire faster than React re-renders) would click the SAME element
      // again, and again, hammering the main thread badly enough to
      // visibly freeze other page animations (reported as the tracking
      // spinner "getting stuck"). A fresh toggle instance from a genuine
      // later re-render (new tab, new lookup) is a different object and
      // isn't blocked by this — only true repeat-clicks on one element are.
      if (!clickedToggles.has(toggle)) {
        var label = normalizeWhitespace(toggle.textContent).toUpperCase();
        if (label.indexOf("SHOW MORE") !== -1) {
          clickedToggles.add(toggle);
          toggle.click();
        }
      }
      toggle.style.setProperty("display", "none", "important");
    });
  }

  // ---------- eBook link -> obvious download button ----------

  function isEbookLink(href) {
    if (!href) return false;
    for (var i = 0; i < EBOOK_PRODUCT_HANDLES.length; i++) {
      if (href.indexOf("/products/" + EBOOK_PRODUCT_HANDLES[i]) !== -1) return true;
    }
    return false;
  }

  function addDownloadButton(rowEl) {
    var row = rowEl.closest(".yq-track-product-item") || rowEl.parentElement;
    if (!row || row.querySelector(".ely-ebook-download-btn")) return;

    var wrap = document.createElement("div");
    wrap.className = "ely-ebook-download-wrap";
    wrap.style.cssText = "margin-top:8px;";

    var btn = document.createElement("a");
    btn.className = "ely-ebook-download-btn";
    btn.href = EBOOK_PDF_URL;
    btn.target = "_blank";
    btn.rel = "noopener";
    btn.style.cssText =
      "display:inline-flex;align-items:center;gap:6px;padding:8px 14px;" +
      "background:#00B7FF;color:#001220;font-weight:700;font-size:13px;" +
      "letter-spacing:0.01em;border-radius:4px;text-decoration:none;";
    btn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M8 2v8m0 0l-3-3m3 3l3-3M3 13h10" stroke="currentColor" stroke-width="1.6" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      "Download the eBook here";

    wrap.appendChild(btn);
    row.appendChild(wrap);
  }

  // Un-links the eBook line item entirely (swaps its <a> for a plain <span>)
  // and adds the obvious download button next to it. Re-runs on every
  // mutation, since 17TRACK re-renders this row (and a fresh linked <a>) on
  // every Show More/Less toggle and tab switch.
  //
  // Deliberately does NOT copy the original <a>'s class list onto the
  // replacement — 17TRACK's blue/underlined "link" look turned out to come
  // from a class-based style rule that doesn't care what tag it's on, so
  // carrying the classes over kept the link appearance even once it wasn't
  // a link anymore. Visual "plain text" styling is forced instead, with
  // !important so it can't lose to whatever classes were nearby.
  function fixEbookLinks() {
    var scope = document.querySelector(".yq-track-order-info-wrap") || document.body;
    var anchors = scope.querySelectorAll("a[href]");
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      if (!isVisible(a)) continue;
      if (!isEbookLink(a.getAttribute("href"))) continue;

      var replacement = document.createElement("span");
      while (a.firstChild) replacement.appendChild(a.firstChild);
      a.parentNode.replaceChild(replacement, a);

      replacement.style.setProperty("color", "inherit", "important");
      replacement.style.setProperty("text-decoration", "none", "important");
      replacement.style.setProperty("cursor", "default", "important");
      // Strip the same "looks like a link" styling from every descendant too,
      // in case the blue/underline was applied a level or two down rather
      // than on the anchor itself.
      var descendants = replacement.querySelectorAll("*");
      for (var d = 0; d < descendants.length; d++) {
        descendants[d].style.setProperty("color", "inherit", "important");
        descendants[d].style.setProperty("text-decoration", "none", "important");
      }

      addDownloadButton(replacement);
    }
  }

  // ---------- Digital-only tracking message ----------

  function findTrackingUnavailableLabel() {
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (node.children.length) continue; // leaf elements only
      if (!isVisible(node)) continue;
      if (/isn.?t available yet/i.test(node.textContent || "")) return node;
    }
    return null;
  }

  // Only touches the message when THIS package is the eBook (signaled by our
  // own download button already being present) — the physical AFM Disabler
  // package's real "not available yet" state (before carrier pickup) is left
  // completely alone.
  function fixDigitalTrackingMessage() {
    var isDigitalPackage = !!document.querySelector(".ely-ebook-download-btn");
    if (!isDigitalPackage) return;

    var label = findTrackingUnavailableLabel();
    if (!label) return;
    if (normalizeWhitespace(label.textContent) === DIGITAL_TRACKING_MESSAGE) return;

    label.textContent = DIGITAL_TRACKING_MESSAGE;
  }

  // A physical ship-to city/state doesn't mean anything for the eBook — hide
  // the whole Destination row on that package rather than showing it with
  // (or without) an address that has nothing to do with a digital download.
  // Same isDigitalPackage signal as the tracking message above.
  function hideDigitalDestinationRow() {
    var isDigitalPackage = !!document.querySelector(".ely-ebook-download-btn");
    if (!isDigitalPackage) return;

    var labelEl = findDestinationLabel();
    var row = labelEl && (labelEl.closest(".yq-track-order-info-item") || labelEl.parentElement);
    if (!row) return;

    row.style.setProperty("display", "none", "important");
  }

  // ---------- Suppress digital-only packages (eBook, Shipping Protection) ----------

  // Shipping Protection has no tracking, destination, or download of its
  // own; the eBook has a download but no physical shipment either — when
  // either lands ALONE in its own package/tab (separate from the AFM
  // Disabler), that tab isn't worth a dedicated tab. Detected generically:
  // "Shipping Protection" text OR the eBook download button, AND exactly
  // one line item total in this package. The one-line-item check matters —
  // if Shopify ever fulfills the eBook together WITH the physical item in
  // the same package, this correctly leaves that combined package alone
  // rather than suppressing a tab that also has real tracking content.
  function isDigitalOnlyPackage() {
    var wrap = document.querySelector(".yq-track-order-info-wrap") || document.body;
    var text = wrap.innerText || "";
    var qtyMatches = text.match(/×\s*\d+/g) || [];
    if (qtyMatches.length > 1) return false;

    var hasEbook = !!document.querySelector(".ely-ebook-download-btn");
    var hasProtection = /shipping protection/i.test(text);
    return hasEbook || hasProtection;
  }

  // Once a digital-only eBook package has been suppressed, its download
  // link is shown here instead — a fixed banner below the tab switcher,
  // visible regardless of which package tab is active, instead of only
  // reachable from a tab that no longer exists.
  var ebookDetected = false;

  // Measures the AFM Disabler's actual rendered thumbnail size, so the
  // eBook banner's own thumbnail can match it exactly rather than guessing
  // a pixel value that might not be right — same "don't guess, verify"
  // approach as the rest of this file's DOM work, just done live instead
  // of via a screenshot round-trip. Skips any image already inside our own
  // banner, in case this runs again after the banner's already inserted.
  function getProductThumbSize() {
    var wrap = document.querySelector(".yq-track-order-info-wrap");
    if (!wrap) return null;
    var imgs = wrap.querySelectorAll("img");
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].closest(".ely-ebook-persistent-banner")) continue;
      var rect = imgs[i].getBoundingClientRect();
      if (rect.width && rect.height) return { width: rect.width, height: rect.height };
    }
    return null;
  }

  // Inserted INSIDE .yq-track-order-info-wrap (the "Order Information"
  // card) as its first section, rather than as a sibling before it — a
  // sibling got caught by whatever grid/flex layout that card's parent
  // uses and rendered as a second, separate box side-by-side with Order
  // Information instead of stacking above it. Living inside the same card,
  // separated by a divider line rather than its own border, reads as one
  // connected box the way the destination/toggle fixes elsewhere in this
  // file already do by editing content within that same wrap.
  function insertPersistentEbookBanner() {
    if (!ebookDetected) return;
    if (document.querySelector(".ely-ebook-persistent-banner")) return;

    var wrap = document.querySelector(".yq-track-order-info-wrap");
    if (!wrap) return;

    var banner = document.createElement("div");
    banner.className = "ely-ebook-persistent-banner";
    banner.style.cssText =
      "margin:0 0 16px;padding-bottom:16px;border-bottom:1px solid #242C3B;" +
      "display:flex;align-items:center;justify-content:space-between;" +
      "gap:12px;flex-wrap:wrap;";

    // Left side: product-card style — thumbnail + text, matching the AFM
    // Disabler line item's own layout below. The image URL comes from
    // window.ElyTrackingConfig (set by elycera-proxy-header.liquid, since
    // this static JS file has no direct access to Shopify product data).
    var left = document.createElement("div");
    left.style.cssText = "display:flex;align-items:center;gap:12px;";

    var ebookImageUrl = window.ElyTrackingConfig && window.ElyTrackingConfig.ebookImageUrl;
    if (ebookImageUrl) {
      var thumb = document.createElement("img");
      thumb.src = ebookImageUrl;
      thumb.alt = "";
      var thumbSize = getProductThumbSize();
      var thumbCss =
        "object-fit:cover;border-radius:6px;border:1px solid #242C3B;flex-shrink:0;";
      thumbCss += thumbSize
        ? "width:" + thumbSize.width + "px;height:" + thumbSize.height + "px;"
        : "width:56px;height:56px;"; // fallback if the AFM thumbnail can't be found/measured yet
      thumb.style.cssText = thumbCss;
      left.appendChild(thumb);
    }

    var label = document.createElement("span");
    label.style.cssText = "color:#D7DCE6;font-weight:600;";
    label.textContent = "Your free eBook — The 22 Pound Margin";
    left.appendChild(label);

    var btn = document.createElement("a");
    // Deliberately NOT "ely-ebook-download-btn" — hideDigitalDestinationRow,
    // fixDigitalTrackingMessage, and isDigitalOnlyPackage all use that exact
    // class to mean "the CURRENT tab's own line item is the eBook"; this
    // banner is sitewide-persistent, so sharing the class would make those
    // checks true on every tab, including the physical one, once inserted.
    btn.className = "ely-ebook-persistent-download-btn";
    btn.href = EBOOK_PDF_URL;
    btn.target = "_blank";
    btn.rel = "noopener";
    btn.style.cssText =
      "display:inline-flex;align-items:center;gap:6px;padding:8px 14px;" +
      "background:#00B7FF;color:#001220;font-weight:700;font-size:13px;" +
      "letter-spacing:0.01em;border-radius:4px;text-decoration:none;";
    btn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M8 2v8m0 0l-3-3m3 3l3-3M3 13h10" stroke="currentColor" stroke-width="1.6" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      "Download the eBook here";

    banner.appendChild(left);
    banner.appendChild(btn);
    wrap.insertBefore(banner, wrap.firstChild);
  }

  // ---------- Package #1/#2 tab styling ----------

  // Which tab's exact text (e.g. "Package #2") is currently selected.
  // Tracked ourselves via clicks rather than read from 17TRACK's own
  // state — neither a guessed CSS class nor aria-selected reflected which
  // tab was actually active, so this makes no assumption about how 17TRACK
  // marks selection internally.
  var activeTabText = null;

  // Tab texts (e.g. "Package #3") identified as Shipping-Protection-only —
  // once discovered, hidden from the tab bar and never landed on again.
  var suppressedTabTexts = {};

  function findPackageTabLabels() {
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, null);
    var node;
    var found = [];
    while ((node = walker.nextNode())) {
      if (node.children.length) continue; // leaf elements only
      if (/^Package #\d+$/i.test(normalizeWhitespace(node.textContent))) {
        found.push(node);
      }
    }
    return found;
  }

  // Capture-phase so this fires regardless of whatever 17TRACK's own click
  // handler does. Never calls preventDefault/stopPropagation — this only
  // observes which tab was clicked, it never interferes with the real
  // tab-switch behavior.
  document.addEventListener(
    "click",
    function (e) {
      var labels = findPackageTabLabels();
      for (var i = 0; i < labels.length; i++) {
        if (labels[i] === e.target || labels[i].contains(e.target)) {
          activeTabText = normalizeWhitespace(labels[i].textContent);
          break;
        }
      }
    },
    true
  );

  // Styles the leaf text element itself (not a guessed wrapper class) into a
  // pill: padding/border-radius/border on that element reads as a pill
  // regardless of what actually wraps it, and clicks still bubble up to
  // whatever ancestor 17TRACK bound its tab-switch handler to, so styling
  // this way can't break the actual tab-switching behavior.
  //
  // Inactive: blue outline, dark (non-blue) fill — the "cage" stays visible
  // against the black page without looking selected. Active: the cage fills
  // solid blue, matching the Track/Download buttons elsewhere on the page.
  // Suppressed (Shipping-Protection-only) tabs are hidden outright.
  function stylePackageTabs() {
    var labels = findPackageTabLabels();
    if (!labels.length) return;

    // A lookup is in flight that will tell us which tab(s) (if any) to
    // suppress — hide the whole switcher until it resolves instead of
    // showing every tab now and having one disappear a moment later.
    // Checks the visible "Tracking..." indicator FIRST: 17TRACK renders the
    // Package #N tabs before the "Tracking Number:" text the
    // lookedUpFor/tabsSettled gate depends on, so relying on that gate
    // alone left a brief window (tabs rendered, our lookup not started
    // yet) where both would flash visible.
    if (findVisibleTrackingLoadingEl() || (lookedUpFor && !tabsSettled)) {
      labels.forEach(function (leaf) {
        leaf.style.setProperty("display", "none", "important");
      });
      return;
    }

    // Before any click, default to the first non-suppressed tab found (the
    // common "first tab selected by default" convention) — self-corrects
    // the instant a real click updates activeTabText.
    if (activeTabText === null) {
      var firstVisible = labels.filter(function (l) {
        return !suppressedTabTexts[normalizeWhitespace(l.textContent)];
      })[0];
      if (firstVisible) activeTabText = normalizeWhitespace(firstVisible.textContent);
    }

    // Only show a tab switcher when there are genuinely multiple real
    // packages to pick between (multiple physical tracking numbers). If
    // suppression leaves just one (or the order only ever had one to begin
    // with), hide the whole bar — a single "Package #1" pill with nothing
    // to switch to serves no purpose.
    var visibleCount = labels.filter(function (l) {
      return !suppressedTabTexts[normalizeWhitespace(l.textContent)];
    }).length;
    if (visibleCount <= 1) {
      labels.forEach(function (leaf) {
        leaf.style.setProperty("display", "none", "important");
      });
      return;
    }

    labels.forEach(function (leaf) {
      var text = normalizeWhitespace(leaf.textContent);

      if (suppressedTabTexts[text]) {
        leaf.style.setProperty("display", "none", "important");
        return;
      }

      var active = text === activeTabText;

      leaf.style.setProperty("display", "inline-block", "important");
      leaf.style.setProperty("padding", "12px 26px", "important");
      leaf.style.setProperty("border-radius", "999px", "important");
      leaf.style.setProperty("border", "2px solid #00B7FF", "important");
      leaf.style.setProperty("background", active ? "#00B7FF" : "#11151D", "important");
      leaf.style.setProperty("color", active ? "#001220" : "#00B7FF", "important");
      leaf.style.setProperty("font-weight", active ? "800" : "700", "important");
      leaf.style.setProperty("font-size", "15px", "important");
      leaf.style.setProperty("cursor", "pointer", "important");
      leaf.style.setProperty("opacity", "1", "important");
      leaf.style.setProperty(
        "box-shadow",
        active ? "0 0 0 4px rgba(0, 183, 255, 0.3)" : "none",
        "important"
      );
    });
  }

  // If the currently-active package turns out to be digital-only (eBook or
  // Shipping Protection, alone), remember its tab as suppressed
  // (stylePackageTabs hides it from then on) and click over to another tab
  // so the customer never actually lands on this near-empty view —
  // including on first page load, if it happens to be the default-active
  // package. If it was the eBook, flags ebookDetected so the persistent
  // download banner takes over showing it.
  function suppressDigitalOnlyTabIfNeeded() {
    if (!activeTabText || !isDigitalOnlyPackage()) return;
    if (suppressedTabTexts[activeTabText]) return;

    if (document.querySelector(".ely-ebook-download-btn")) ebookDetected = true;
    suppressedTabTexts[activeTabText] = true;

    var labels = findPackageTabLabels();
    var other = labels.filter(function (l) {
      return !suppressedTabTexts[normalizeWhitespace(l.textContent)];
    })[0];
    if (other) {
      activeTabText = normalizeWhitespace(other.textContent);
      other.click();
    }
  }

  // ---------- Un-link "Powered by 17TRACK" ----------

  // Same technique as fixEbookLinks: swap the <a> for a plain <span>
  // carrying its content, so it's no longer clickable. Matched by link text
  // / host rather than a class name, and doesn't force a new text color —
  // this branding line was already styled as muted/subtle, not a bright
  // link, so there's no "still looks clickable" leftover to fix.
  function unlinkPoweredBy17Track() {
    var anchors = document.querySelectorAll("a[href]");
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      if (!isVisible(a)) continue;
      var text = normalizeWhitespace(a.textContent);
      var href = a.getAttribute("href") || "";
      if (text !== "17TRACK" && href.indexOf("17track.net") === -1) continue;

      var replacement = document.createElement("span");
      while (a.firstChild) replacement.appendChild(a.firstChild);
      a.parentNode.replaceChild(replacement, a);

      replacement.style.setProperty("text-decoration", "none", "important");
      replacement.style.setProperty("cursor", "default", "important");
    }
  }

  // ---------- Center the "Tracking..." loading state ----------

  // 17TRACK's own in-progress indicator (text + a package icon) wasn't
  // centered as a unit. Matched by exact text like everything else here.
  //
  // Deliberately does NOT set `display` on the container, even though
  // flexbox would be the easy way to center it — an earlier version forced
  // display:flex !important on it, and that's almost certainly what made
  // this indicator appear permanently, even with no lookup in progress and
  // even on the bare form page: if 17TRACK shows/hides this same container
  // by toggling ITS OWN display property (none <-> flex/block) depending on
  // whether a lookup is actually running, an !important override on that
  // exact property defeats that toggle and forces it visible all the time.
  // Centering is done here without touching display at all: text-align on
  // the container handles the text, and a block+auto-margin icon centers
  // itself under it — neither property affects visibility.
  //
  // Shared with stylePackageTabs (#5): the visible "Tracking..." indicator
  // is also the earliest reliable signal that a lookup is in progress —
  // 17TRACK renders the Package #N tabs BEFORE the "Tracking Number:" text
  // this file's other lookup-in-flight gate depends on, so relying only on
  // that gate left a brief window where both tabs would flash visible.
  function findVisibleTrackingLoadingEl() {
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (node.children.length) continue;
      if (!isVisible(node)) continue;
      if (normalizeWhitespace(node.textContent) === "Tracking...") return node;
    }
    return null;
  }

  function fixTrackingLoadingAlignment() {
    var textEl = findVisibleTrackingLoadingEl();
    if (!textEl) return;

    textEl.style.setProperty("text-align", "center", "important");

    var container = textEl.parentElement;
    if (container) {
      container.style.setProperty("text-align", "center", "important");

      // Center EVERY sibling of the text, not just <img>/<svg> — an
      // earlier version only matched those two tags specifically, which
      // missed the icon whenever 17TRACK happened to render it as some
      // other element (span/div/i, an icon font, etc.), leaving it
      // off-center in exactly the cases that mattered.
      var children = container.children;
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        if (child === textEl) continue;
        child.style.setProperty("display", "block", "important");
        child.style.setProperty("margin", "8px auto 0", "important");
      }
    }
  }

  // ---------- TEMPORARY: report real layout data for the mobile
  // Destination-overflow bug (safe to delete once resolved) ----------

  // There's no browser/devtools tool available to inspect the live
  // rendered page directly, and guessing at CSS fixes blind clearly
  // hasn't worked — this sends the ACTUAL computed styles/dimensions of
  // the Destination row and its ancestors to a debug endpoint once per
  // page load, so the real cause can be read back server-side instead of
  // speculated about.
  var debugSent = false;

  // getBoundingClientRect() on the ELEMENT reports its border box, which
  // does not move when padding changes under box-sizing:border-box — it
  // can confirm a CSS rule landed but not whether the actual rendered
  // TEXT gained any visual breathing room. Range.getBoundingClientRect()
  // on the element's contents measures the real visible text edges
  // instead, padding and all.
  function measureTextRect(el) {
    try {
      var range = document.createRange();
      range.selectNodeContents(el);
      var r = range.getBoundingClientRect();
      return { left: Math.round(r.left), right: Math.round(r.right), width: Math.round(r.width) };
    } catch (e) {
      return null;
    }
  }

  function describeElForDebug(el) {
    if (!el) return null;
    var cs = window.getComputedStyle(el);
    var rect = el.getBoundingClientRect();
    return {
      tag: el.tagName,
      className: typeof el.className === "string" ? el.className : "",
      text: normalizeWhitespace(el.textContent).slice(0, 140),
      rect: {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
        left: Math.round(rect.left),
        right: Math.round(rect.right),
      },
      textRect: measureTextRect(el),
      display: cs.display,
      whiteSpace: cs.whiteSpace,
      overflow: cs.overflow,
      overflowX: cs.overflowX,
      flexWrap: cs.flexWrap,
      flexDirection: cs.flexDirection,
      minWidth: cs.minWidth,
      maxWidth: cs.maxWidth,
      width: cs.width,
      fontSize: cs.fontSize,
      paddingLeft: cs.paddingLeft,
      paddingRight: cs.paddingRight,
      boxSizing: cs.boxSizing,
    };
  }

  function sendMobileDebugInfoOnce() {
    if (debugSent) return;
    // Wait until cachedDestination is actually set (the backend lookup has
    // resolved and applyCachedDestination has run) — otherwise this could
    // fire before the city/state text is even appended, capturing the
    // pre-fix state instead of what's actually causing the overflow.
    if (!cachedDestination) return;
    var labelEl = findDestinationLabel();
    var valueEl = labelEl && findValueElement(labelEl);
    if (!valueEl) return;
    debugSent = true;

    var row = valueEl.closest(".yq-track-order-info-item");
    var wrap = document.querySelector(".yq-track-order-info-wrap");

    // Every row in the card, not just Destination — the mobile-spacing fix
    // (fixOrderInfoMobileSpacing) is now applied uniformly, so this checks
    // whether ALL of them actually got the breathing room, not just the
    // one that happened to be reported.
    var allRows = wrap
      ? wrap.querySelectorAll(".yq-track-order-info-item, .yq-track-product-item")
      : [];
    var allRowsInfo = [];
    for (var i = 0; i < allRows.length; i++) {
      allRowsInfo.push(describeElForDebug(allRows[i]));
    }

    var payload = {
      note: "mobile-destination-overflow",
      viewportWidth: window.innerWidth,
      documentWidth: document.documentElement.clientWidth,
      value: describeElForDebug(valueEl),
      label: describeElForDebug(labelEl),
      row: describeElForDebug(row),
      rowParent: describeElForDebug(row && row.parentElement),
      wrap: describeElForDebug(wrap),
      rowOuterHTML: row ? row.outerHTML.slice(0, 1500) : null,
      allRows: allRowsInfo,
    };

    fetch("https://elycera-chat-agent.onrender.com/api/debug-log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(function () {});
  }

  // ---------- shared scan loop ----------

  function scanPage() {
    var bodyText = document.body.innerText || "";
    var orderMatch = bodyText.match(/Order Number\s*#?\s*(\d+)/i);
    var trackingMatch = bodyText.match(/Tracking Number[:\s]+([A-Za-z0-9]{6,})/i);

    unlinkPoweredBy17Track();
    fixTrackingLoadingAlignment();
    forceExpandOrderInfo();
    if (orderMatch && trackingMatch) lookupDestination(orderMatch[1], trackingMatch[1]);

    applyCachedDestination();
    fixEbookLinks();
    fixDigitalTrackingMessage();
    hideDigitalDestinationRow();
    suppressDigitalOnlyTabIfNeeded();
    stylePackageTabs();
    insertPersistentEbookBanner();
    fixOrderInfoMobileSpacing();
    sendMobileDebugInfoOnce();
  }

  // Coalesces bursts of mutations into at most one scanPage() per animation
  // frame. scanPage does several full-document TreeWalker passes; without
  // this, a page that mutates frequently (17TRACK's own polling/animation)
  // could run all of them many times per second, saturating the main
  // thread enough to visibly freeze other animations (reported as the
  // tracking spinner "getting stuck") — a second line of defense alongside
  // the click-once guards above, not reliant on them being sufficient.
  var scanScheduled = false;
  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    window.requestAnimationFrame(function () {
      scanScheduled = false;
      scanPage();
    });
  }

  var observer = new MutationObserver(scheduleScan);

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.addEventListener("load", scheduleScan);
})();
